import {
    Cell,
    Column,
    ColumnSizingState,
    flexRender,
    Header,
    Table as ReactTable,
    Row,
    SortDirection,
} from '@tanstack/react-table';
import React from 'react';
import { flushSync } from 'react-dom';
import { LuArrowUpDown, LuChevronDown, LuChevronRight, LuChevronUp } from 'react-icons/lu';

import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableFooter, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { TableSkeletonRow } from '@/components/ui/table-skeleton-row';
import { TooltipProvider } from '@/components/ui/tooltip';
import { cn } from '@/utils/cn';

import { ColumnFilter } from './ColumnFilter';
import { CellWithTooltip, HeaderWithTooltip } from './TruncatedCellWithTooltip';
import { TablePresentationProps } from './types';
import { isDataColumn } from './useDataTable';

interface TableRendererProps<T> extends TablePresentationProps<T> {
    table: ReactTable<T>;
}

function SortIcon({ direction, className }: { direction: false | SortDirection; className?: string }) {
    const Icon = direction === 'asc' ? LuChevronUp : direction === 'desc' ? LuChevronDown : LuArrowUpDown;
    return (
        <Icon
            aria-hidden="true"
            style={{ width: 14, height: 14 }}
            className={cn(
                'shrink-0 transition-opacity',
                className,
                direction
                    ? 'text-gray-700'
                    : 'text-gray-400 opacity-0 group-focus-within/header:opacity-100 group-hover/header:opacity-100',
            )}
        />
    );
}

/** The rightmost data column without a user-set width; in fixed layout it takes up the leftover space. */
function findAbsorbingColumn<T>(columns: Column<T, unknown>[], columnSizing: ColumnSizingState) {
    return [...columns]
        .reverse()
        .find((column) => isDataColumn(column.columnDef) && columnSizing[column.id] === undefined);
}

function sizesToContent<T>(column: Column<T, unknown>, columnSizing: ColumnSizingState) {
    return !!column.columnDef.meta?.sizeToContent && columnSizing[column.id] === undefined;
}

type ResizeStartEvent = React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>;

// Starts on the first pointer move so a click never changes sizing, and seeds rendered widths so nothing jumps.
function startColumnResize<T>(
    table: ReactTable<T>,
    header: Header<T, unknown>,
    useFixedLayout: boolean,
    event: ResizeStartEvent,
) {
    const isTouch = 'touches' in event;
    if (isTouch && event.touches.length > 1) return;
    const clientX = isTouch ? event.touches[0].clientX : event.clientX;
    const contextDocument = event.currentTarget.ownerDocument;
    // Listening on the window during capture lets TanStack's document listener also receive that first move.
    const contextWindow = contextDocument.defaultView ?? window;
    const headerCells = event.currentTarget
        .closest('table')
        ?.querySelectorAll<HTMLTableCellElement>('thead th[data-column-id]');
    const moveEvent = isTouch ? 'touchmove' : 'mousemove';
    const endEvent = isTouch ? 'touchend' : 'mouseup';

    function stopWaiting() {
        contextWindow.removeEventListener(moveEvent, begin, true);
        contextWindow.removeEventListener(endEvent, stopWaiting, true);
    }

    function begin() {
        stopWaiting();
        const draggedId = header.column.id;
        const absorbingId = useFixedLayout
            ? undefined
            : findAbsorbingColumn(table.getVisibleLeafColumns(), {
                  ...table.getState().columnSizing,
                  [draggedId]: 0,
              })?.id;
        const renderedSizes: Record<string, number> = {};
        headerCells?.forEach((cell) => {
            const id = cell.dataset.columnId!;
            const width = cell.getBoundingClientRect().width;
            const shouldMeasure = id === draggedId || (!useFixedLayout && id !== absorbingId);
            if (shouldMeasure && table.getColumn(id)?.columns.length === 0 && width > 0) {
                renderedSizes[id] = width;
            }
        });
        if (Object.keys(renderedSizes).length > 0) {
            flushSync(() => table.setColumnSizing((sizing) => ({ ...sizing, ...renderedSizes })));
        }
        // Commit the resize start before TanStack's move listener reads it from state.
        flushSync(() =>
            header.getResizeHandler(contextDocument)(
                isTouch ? { type: 'touchstart', touches: [{ clientX }] } : { type: 'mousedown', clientX },
            ),
        );
    }

    contextWindow.addEventListener(moveEvent, begin, true);
    contextWindow.addEventListener(endEvent, stopWaiting, true);
}

function ariaSort<T>(column: Column<T, unknown>): React.AriaAttributes['aria-sort'] {
    if (!column.getCanSort()) return undefined;
    const direction = column.getIsSorted();
    return direction === 'asc' ? 'ascending' : direction === 'desc' ? 'descending' : 'none';
}

function ColumnResizeHandle<T>({
    table,
    header,
    useFixedLayout,
}: {
    table: ReactTable<T>;
    header: Header<T, unknown>;
    useFixedLayout: boolean;
}) {
    return (
        <div
            aria-label={`Resize ${header.column.id}`}
            onMouseDown={(event) => startColumnResize(table, header, useFixedLayout, event)}
            onTouchStart={(event) => startColumnResize(table, header, useFixedLayout, event)}
            className={cn(
                'group/resize absolute inset-y-0 z-10 flex w-2 cursor-col-resize touch-none items-center justify-center select-none',
                // Straddle the column border, except on the last column where it would overflow the table.
                header.column.getIsLastColumn() ? 'right-0' : '-right-1',
            )}
        >
            <div
                className={cn(
                    'h-5 w-px transition-colors duration-200',
                    header.column.getIsResizing()
                        ? 'bg-cocoa-500'
                        : 'group-hover/thead:bg-cocoa-200 group-hover/thead:group-hover/resize:bg-cocoa-400 bg-transparent',
                )}
            />
        </div>
    );
}

interface DataTableHeaderCellProps<T> {
    table: ReactTable<T>;
    header: Header<T, unknown>;
    width: number | undefined;
    useFixedLayout: boolean;
    shrinkHeaders: boolean;
    truncateHeaders: boolean;
    truncateCells: boolean;
}

function DataTableHeaderCell<T>({
    table,
    header,
    width,
    useFixedLayout,
    shrinkHeaders,
    truncateHeaders,
    truncateCells,
}: DataTableHeaderCellProps<T>) {
    const { column } = header;
    const alignRight = column.columnDef.meta?.align === 'right';
    const renderedHeader = flexRender(column.columnDef.header, header.getContext());
    // Display column headers hold controls such as checkboxes, which must not be clipped.
    const headerLabel =
        shrinkHeaders && isDataColumn(column.columnDef) ? (
            <HeaderWithTooltip observeResize>{renderedHeader}</HeaderWithTooltip>
        ) : (
            renderedHeader
        );

    return (
        <TableHead
            data-column-id={column.id}
            aria-sort={ariaSort(column)}
            className={cn(
                'group/header relative font-medium text-gray-700',
                alignRight && 'text-right',
                // In auto layout the unwrapped header sets each column's minimum width.
                truncateCells && !useFixedLayout && 'whitespace-nowrap',
                column.columnDef.meta?.headerClassName,
            )}
            style={{ width }}
        >
            {header.isPlaceholder ? null : (
                <>
                    <div className={cn('flex items-center gap-1', alignRight && 'flex-row-reverse')}>
                        <div
                            className={cn(
                                'flex items-center gap-1',
                                alignRight && 'flex-row-reverse',
                                shrinkHeaders && 'min-w-0',
                                truncateHeaders && 'flex-1',
                            )}
                        >
                            {column.getCanSort() ? (
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className={cn(
                                        'h-auto p-0 font-medium text-gray-700 hover:bg-transparent hover:text-gray-700',
                                        alignRight && 'flex-row-reverse',
                                        shrinkHeaders && 'min-w-0 justify-start overflow-hidden',
                                        shrinkHeaders && (alignRight ? 'text-right' : 'text-left'),
                                        truncateHeaders && 'flex-1',
                                    )}
                                    style={{
                                        color: 'inherit',
                                        fontSize: 'inherit',
                                        fontWeight: 'inherit',
                                        letterSpacing: 'inherit',
                                        textTransform: 'inherit',
                                    }}
                                    onClick={column.getToggleSortingHandler()}
                                >
                                    {headerLabel}
                                    <SortIcon
                                        direction={column.getIsSorted()}
                                        className={alignRight ? 'mr-1' : 'ml-1'}
                                    />
                                </Button>
                            ) : (
                                headerLabel
                            )}
                        </div>
                        {column.getCanFilter() && <ColumnFilter column={column} table={table} />}
                    </div>
                    {column.getCanResize() && (
                        <ColumnResizeHandle table={table} header={header} useFixedLayout={useFixedLayout} />
                    )}
                </>
            )}
        </TableHead>
    );
}

function GroupToggle<T>({ row }: { row: Row<T> }) {
    const label = String(row.groupingValue ?? 'Unspecified');
    return (
        <button
            type="button"
            className="flex items-center gap-2 font-semibold whitespace-nowrap"
            aria-label={`${row.getIsExpanded() ? 'Collapse' : 'Expand'} ${label} group`}
            onClick={(event) => {
                event.stopPropagation();
                row.toggleExpanded();
            }}
        >
            {row.getIsExpanded() ? <LuChevronDown className="h-4 w-4" /> : <LuChevronRight className="h-4 w-4" />}
            {label}
            <span className="text-muted-foreground font-normal">({row.subRows.length})</span>
        </button>
    );
}

function renderCellContent<T>(cell: Cell<T, unknown>, row: Row<T>, groupLabelColumnId?: string): React.ReactNode {
    if (!row.getIsGrouped()) return flexRender(cell.column.columnDef.cell, cell.getContext());
    if (cell.column.id === groupLabelColumnId) return <GroupToggle row={row} />;
    const { aggregatedCell } = cell.column.columnDef;
    return aggregatedCell ? flexRender(aggregatedCell, cell.getContext()) : null;
}

/** Renders a TanStack table without owning its data operations, state, toolbar, or pagination. */
export function TableRenderer<T>({
    table,
    isLoading = false,
    isFetching = false,
    loadingRowCount = 3,
    emptyMessage = 'No data found',
    onRowClick,
    rowClassName = '',
    truncateHeaders = true,
    truncateCells = false,
    renderRowDetails,
}: TableRendererProps<T>) {
    const rows = table.getRowModel().rows;
    const visibleColumns = table.getVisibleLeafColumns();
    const visibleColumnCount = visibleColumns.length;
    const columnSizing = table.getState().columnSizing;
    const absorbingColumnId = findAbsorbingColumn(visibleColumns, columnSizing)?.id;
    const groupLabelColumnId = visibleColumns.find((column) => isDataColumn(column.columnDef))?.id;
    const hasResized = visibleColumns.some((column) => columnSizing[column.id] !== undefined);
    // Fixed layout would split leftover space evenly among unsized columns, ignoring header and content, so
    // content-sized columns keep auto layout until a resize gives every column but the absorbing one a width.
    const useFixedLayout = visibleColumns.every(
        (column) => !sizesToContent(column, columnSizing) || (column.id === absorbingColumnId && hasResized),
    );
    // Fixed widths can be narrower than a header, so headers must shrink with an ellipsis.
    const shrinkHeaders = truncateHeaders || useFixedLayout;
    const footerGroups = table.getFooterGroups();
    const hasFooter = footerGroups.some((group) => group.headers.some((header) => header.column.columnDef.footer));
    const headerWidth = (column: Column<T, unknown>) =>
        column.id === absorbingColumnId || sizesToContent(column, columnSizing) ? undefined : column.getSize();

    return (
        <TooltipProvider>
            <Table
                style={
                    useFixedLayout
                        ? {
                              // Once every data column has a user-set width, nothing absorbs leftover space.
                              width: absorbingColumnId ? '100%' : table.getCenterTotalSize(),
                              minWidth: table.getCenterTotalSize(),
                              tableLayout: 'fixed',
                          }
                        : { width: '100%', tableLayout: 'auto' }
                }
            >
                <TableHeader className="group/thead">
                    {table.getHeaderGroups().map((headerGroup) => (
                        <TableRow key={headerGroup.id}>
                            {headerGroup.headers.map((header) => (
                                <DataTableHeaderCell
                                    key={header.id}
                                    table={table}
                                    header={header}
                                    width={headerWidth(header.column)}
                                    useFixedLayout={useFixedLayout}
                                    shrinkHeaders={shrinkHeaders}
                                    truncateHeaders={truncateHeaders}
                                    truncateCells={truncateCells}
                                />
                            ))}
                        </TableRow>
                    ))}
                </TableHeader>
                <TableBody>
                    {!isLoading && rows.length === 0 && (
                        <TableRow>
                            <TableCell colSpan={visibleColumnCount} className="h-24 text-center">
                                <p className="text-muted-foreground">{emptyMessage}</p>
                            </TableCell>
                        </TableRow>
                    )}
                    {isLoading
                        ? Array.from({ length: loadingRowCount }).map((_, i) => (
                              <TableSkeletonRow key={i} columns={visibleColumnCount} />
                          ))
                        : rows.map((row) => (
                              <React.Fragment key={row.id}>
                                  <TableRow
                                      className={cn(
                                          'hover:bg-gray-50',
                                          row.getIsGrouped() && 'bg-cocoa-100 hover:bg-cocoa-100',
                                          typeof rowClassName === 'function'
                                              ? rowClassName(row.original, row)
                                              : rowClassName,
                                          onRowClick && 'cursor-pointer',
                                      )}
                                      onClick={() => onRowClick?.(row.original)}
                                  >
                                      {row.getVisibleCells().map((cell) => {
                                          const content = renderCellContent(cell, row, groupLabelColumnId);
                                          const truncated =
                                              truncateCells && isDataColumn(cell.column.columnDef) ? (
                                                  <CellWithTooltip>{content}</CellWithTooltip>
                                              ) : (
                                                  content
                                              );
                                          const isGroupedLeaf =
                                              cell.column.id === groupLabelColumnId &&
                                              !!row.getParentRow()?.getIsGrouped();
                                          return (
                                              <TableCell
                                                  key={cell.id}
                                                  className={cn(
                                                      cell.column.columnDef.meta?.align === 'right' && 'text-right',
                                                      cell.column.columnDef.meta?.cellClassName,
                                                  )}
                                              >
                                                  {isGroupedLeaf ? <div className="pl-6">{truncated}</div> : truncated}
                                              </TableCell>
                                          );
                                      })}
                                  </TableRow>
                                  {renderRowDetails && !row.getIsGrouped() && row.getIsExpanded() && (
                                      <TableRow className="hover:bg-transparent">
                                          <TableCell colSpan={row.getVisibleCells().length} className="bg-gray-50 p-0">
                                              {renderRowDetails(row.original)}
                                          </TableCell>
                                      </TableRow>
                                  )}
                              </React.Fragment>
                          ))}
                </TableBody>
                {hasFooter && !isLoading && (
                    <TableFooter>
                        {footerGroups.map((footerGroup) => (
                            <TableRow key={footerGroup.id} className="hover:bg-transparent">
                                {footerGroup.headers.map((header) => (
                                    <TableCell
                                        key={header.id}
                                        className={cn(
                                            header.column.columnDef.meta?.align === 'right' && 'text-right',
                                            header.column.columnDef.meta?.cellClassName,
                                        )}
                                    >
                                        {header.isPlaceholder
                                            ? null
                                            : flexRender(header.column.columnDef.footer, header.getContext())}
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))}
                    </TableFooter>
                )}
            </Table>
            {isFetching && !isLoading && (
                <div
                    role="progressbar"
                    aria-label="Refreshing data"
                    className="h-1 w-full animate-pulse bg-gradient-to-r from-gray-300 via-blue-500 to-gray-300"
                />
            )}
        </TooltipProvider>
    );
}
